import { useNavigate, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import HelpText from './HelpText';
import CardHeader from '../../components/CardHeader';
import UpInput from '../../components/Common/UpInput';
import UpButton from '../../components/Common/UpButton';
import {
  checkErrorTypeString,
  isValidPhoneNumberOrEmail,
} from '../../helpers/utils';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import CountryCodeSelect from '../../components/CountryCodeSelect';
import { getBaseUrlPrime } from '../../helpers/environment';
import UpPopover from '../../components/Common/UpPopover/index.jsx';
import { verifyUser } from '../../api/auth.js';
import UpLoadingCover from '../../components/Common/UpLoadingCover/index.jsx';
import UpLoadingBar from '../../components/Common/UpLoadingBar/index.jsx';
import InfoIcon from '../../assets/icons/InfoIcon.jsx';
import AnalyticsService from '../../services/analytics.js';

function EmailMobileView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countryCode, setCountryCode] = useState(
    searchParams.get('isdCode') || '',
  );
  const [emailMobile, setEmailMobile] = useState(
    searchParams.get('email') || searchParams.get('mobile') || '',
  );
  const redirectApp = LocalStorageHelper.getRedirectApp();
  const [emailMobileError, setEmailMobileError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [pendingInvite, setPendingInvite] = useState(false);

  const isInputANumber = emailMobile?.length >= 2 && !isNaN(emailMobile);
  const isdCodeGeo = LocalStorageHelper.getIsdCodeGeo();

  const hideLoginFallback = LocalStorageHelper.getUserFeatureGroup() <= 100;
  const hideAppLoginFallback = LocalStorageHelper.getUserFeatureGroup() <= 100;

  useEffect(() => {
    const loginApp = searchParams.get('app');
    const isEdcDevice = searchParams.get('isEDC');
    const redirectApp = searchParams.get('redirect');
    const isOrdermarkDevice = searchParams.get('isOrderMarkDevice');
    const redirectHost = searchParams.get('host');

    if (!LocalStorageHelper.getAppLogin()) {
      LocalStorageHelper.setAppLogin(loginApp);
    }

    if (redirectApp) {
      LocalStorageHelper.setRedirectApp(redirectApp);
    }

    if (redirectHost) {
      LocalStorageHelper.setRedirectHost(redirectHost);
    } else {
      // Prevent new request from using the old redirect host
      LocalStorageHelper.resetRedirectHost();
    }

    if (isEdcDevice) {
      LocalStorageHelper.setEdcDevice(Number(isEdcDevice));
    }

    if (isOrdermarkDevice) {
      LocalStorageHelper.setOrdermarkDevice(Number(isOrdermarkDevice));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // run this effect only if countryCode changes
    onInputBlur();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryCode]);

  function handleSubmit(e) {
    e.preventDefault();

    if (!isValidPhoneNumberOrEmail(emailMobile, countryCode)) {
      setEmailMobileError('Please enter valid email or mobile number');
      return;
    }

    if (emailMobileError) {
      return;
    }
    const params = {};
    if (!isNaN(emailMobile)) {
      params.phone = emailMobile;
      params.isd_code = countryCode;
    } else {
      params.email = emailMobile;
    }
    verifyLoginUser(params);
  }

  function onInputBlur() {
    if (!emailMobile) {
      return;
    }

    if (isValidPhoneNumberOrEmail(emailMobile, countryCode)) {
      setEmailMobileError('');
      return;
    }

    setEmailMobileError('Please enter valid email or mobile number');
  }

  function onInputChange(updatedValue) {
    setPendingInvite(false);
    setErrorMessage('');
    setEmailMobileError('');
    setEmailMobile(updatedValue);
  }
  
  function onVerifySuccess() {
    LocalStorageHelper.setUserDetails({
      countryCode,
      emailMobile,
      isMobile: isInputANumber,
      selectedLogin: isInputANumber
        ? `${countryCode}${emailMobile}`
        : emailMobile,
    });

    navigate('/login/password');
  }

  function onVerifyError(err) {
    const errorMessage = checkErrorTypeString(err?.response?.data?.message);
    setErrorMessage(errorMessage);
    if (err?.response?.data?.has_pending_invite) {
      setPendingInvite(true);
      LocalStorageHelper.setUserDetails({
        countryCode,
        emailMobile,
        isMobile: isInputANumber,
        selectedLogin: isInputANumber
          ? `${countryCode}${emailMobile}`
          : emailMobile,
      });
      navigate('/login/pending-invite');
    }
  }

  const { isLoading: loading, mutate: verifyLoginUser } = useMutation({
    mutationFn: verifyUser,
    onError: onVerifyError,
    onSuccess: onVerifySuccess,
  });

  return (
    <div>
      <UpLoadingBar loading={loading} />
      <div className="dark-view-wrapper relative">
        <UpLoadingCover loading={loading} />
        <div className="flex-grow mb-4">
          <div className="flex flex-row justify-between">
            <CardHeader title="Login" />
            {redirectApp === 'app' && (
              <UpPopover
                arrowAlign="start"
                contentSide="right"
                content={
                  <div className="py-2">
                    <UpButton
                      variant="link"
                      target="_self"
                      wrapperClass="w-max mx-auto"
                      href={`/login/old-account`}>
                      Login to old account
                    </UpButton>
                  </div>
                }>
                ...
              </UpPopover>
            )}
          </div>
          <form onSubmit={handleSubmit}>
            <UpInput
              autoFocus
              type="text"
              name="email_mobile"
              wrapperClass="mt-12 mb-8"
              label="Email/Mobile Number"
              placeholder={`Ex: hello@example.com or ${isdCodeGeo} 999-999-9999`}
              value={emailMobile}
              onBlur={onInputBlur}
              onChange={onInputChange}
              errorContent={emailMobileError || errorMessage}
              showExtraInput={isInputANumber}
              extraInput={
                <CountryCodeSelect
                  value={countryCode}
                  setCountryCode={setCountryCode}
                />
              }
            />
            {!pendingInvite && errorMessage && (
              <div className="text-xs bg-up-blue-50 p-3 mb-8 rounded">
                <div className="flex gap-2 ">
                  <InfoIcon className="mt-0.5 text-up-blue-dark" />
                  <div>
                    <div className="font-semibold text-up-clr-neutral-black-80">
                      Request an invite from your admin
                    </div>
                    <div className="text-up-clr-neutral-black-60">
                      To get access, please ask your business admin to send you
                      an invite. Once you accept it, you can log in using the
                      same email or mobile number.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <UpButton
              disabled={emailMobileError || errorMessage}
              type="submit"
              wrapperClass={redirectApp === 'atlas' ? 'mb-5' : ''}>
              Continue
            </UpButton>
          </form>
          {redirectApp && redirectApp !== 'atlas' && (
            <p className="text-up-clr-light-grey text-center mt-4 mb-2">or</p>
          )}
          {redirectApp === 'prime-web' &&
            (hideLoginFallback ? (
              <UpButton
                variant="link"
                target="_self"
                wrapperClass="w-max mx-auto"
                href={`/login/prime`}>
                Login with Prime Credentials
              </UpButton>
            ) : (
              <UpButton
                variant="link"
                target="_self"
                wrapperClass="w-max mx-auto"
                href={`${getBaseUrlPrime()}/auth/login/?reset_ule=42`}>
                Take me back to old Prime login
              </UpButton>
            ))}
          {redirectApp === 'app' &&
            (hideAppLoginFallback ? (
              <UpButton
                variant="link"
                target="_self"
                wrapperClass="w-max mx-auto"
                href={`/login/prime`}>
                Login with Prime Credentials
              </UpButton>
            ) : (
              <UpButton
                variant="link"
                target="_self"
                wrapperClass="w-max mx-auto"
                href={`/cancel`}>
                Take me back to old Prime login
              </UpButton>
            ))}
        </div>
        <HelpText screen="landing" prefilled={{ loginCred: emailMobile }} />
      </div>
    </div>
  );
}

export default EmailMobileView;
